/**
 * InlineToolSelector
 *
 * Embeds the prioritization decision tree directly in the Tools page.
 * Skips the ScenarioSelector (starts at Q1) since the tools page already
 * provides that context. Results are shown inline with direct links to the
 * integrated /prioritization/tools/[slug] routes.
 *
 * Audit trail: every completed recommendation is persisted to
 * tool_selector_events via toolSelectorStore. Tool opens are recorded
 * in-place on the same row.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Wand2,
  ArrowRight,
  CheckCircle2,
  ArrowLeft,
  Sparkles,
} from 'lucide-react';
import { createDecisionEngine } from '../../utils/prioritization-selector/decisionEngine';
import { getQuestionById } from '../../data/prioritization-selector/decisionTree';
import { useToolSelectorStore } from '../../stores/prioritization/toolSelectorStore';
import {
  getRecommendationsForConfiguration,
  OBJECT_TYPE_LABELS,
  OUTPUT_SHAPE_HINTS,
  type ObjectType,
  type OutputShape,
} from '../../lib/toolRecommendationMatrix';
import { useWorkspaceModules, isToolEnabled } from '../../lib/workspaceModulesService';
import type { UserResponse, BundleRecommendation } from '../../types/prioritization-selector';
import type { ToolSelectorResponse } from '../../types/prioritization/toolSelectorEvents';

// Maps selector method IDs → /prioritization/tools/[slug]
const METHOD_TO_TOOL_PATH: Record<string, string> = {
  'impact-effort-2x2': 'impact-effort-matrix',
  'eisenhower': 'eisenhower-matrix',
  'moscow': 'moscow-method',
  'rice': 'rice-scoring',
  'wsjf': 'wsjf',
  'stack-rank': 'stack-ranking',
  'pairwise': 'pairwise-ranking',
  'dot-voting': 'dot-voting',
  'buy-a-feature': 'buy-a-feature',
  'pareto': 'pareto-analysis',
  'cost-of-delay': 'cost-of-delay',
  'simple-weighted': 'simple-weighted-scoring',
  'ahp': 'ahp',
};

const COMPLEXITY_STYLES: Record<string, string> = {
  simple: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  moderate: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  complex: 'bg-[var(--coral-dim)] text-[var(--coral)]',
};

const TIME_STYLES: Record<string, string> = {
  quick: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  moderate: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  extensive: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
};

interface InlineState {
  currentQuestion: string;
  responses: UserResponse[];
  path: string[];
  isComplete: boolean;
  recommendation: BundleRecommendation | null;
}

const initialState: InlineState = {
  currentQuestion: 'Q1',
  responses: [],
  path: [],
  isComplete: false,
  recommendation: null,
};

/** Build the auditable Q&A trail from engine responses */
function buildAuditResponses(responses: UserResponse[]): ToolSelectorResponse[] {
  return responses.map(r => {
    const question = getQuestionById(r.questionId);
    const option = question?.options?.find(o => o.value === r.value);
    return {
      questionId: r.questionId,
      questionText: question?.text ?? r.questionId,
      selectedValue: String(r.value),
      selectedText: option?.text ?? String(r.value),
    };
  });
}

interface InlineToolSelectorProps {
  /** Object types from the active configuration — enables matrix recommendations */
  objectTypes?: ObjectType[];
  /** Output shapes from the active configuration — enables matrix recommendations */
  outputShapes?: OutputShape[];
}

export default function InlineToolSelector({ objectTypes, outputShapes }: InlineToolSelectorProps = {}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<InlineState>(initialState);
  const workspaceFeatures = useWorkspaceModules();

  // Matrix recommendations when configuration metadata is present — filtered to enabled tools only
  const rawMatrixRecs = (objectTypes?.length && outputShapes?.length)
    ? getRecommendationsForConfiguration(objectTypes, outputShapes)
    : null;
  const matrixRecs = rawMatrixRecs
    ? {
        ...rawMatrixRecs,
        primaryToolIds: rawMatrixRecs.primaryToolIds.filter((id) => isToolEnabled(workspaceFeatures, id)),
      }
    : null;

  // Stores the persisted event ID so tool-open updates reference it
  const eventIdRef = useRef<string | null>(null);
  // Guard: only log once per recommendation (state.isComplete can re-render multiple times)
  const recommendationLoggedRef = useRef(false);

  const logRecommendation = useToolSelectorStore(s => s.logRecommendation);
  const logToolOpened = useToolSelectorStore(s => s.logToolOpened);

  // Persist the recommendation as soon as it becomes available
  useEffect(() => {
    if (!state.isComplete || !state.recommendation || recommendationLoggedRef.current) return;
    recommendationLoggedRef.current = true;

    const auditResponses = buildAuditResponses(state.responses);

    logRecommendation({
      responses: auditResponses,
      decisionPath: state.path,
      recommendedMethodIds: state.recommendation.recommendedMethods.map(m => m.id),
      recommendedMethodNames: state.recommendation.recommendedMethods.map(m => m.name),
      confidence: state.recommendation.confidence as 'high' | 'medium' | 'low' | null,
      reasoning: state.recommendation.reasoning ?? null,
      source: 'tools-page',
    }).then(id => {
      eventIdRef.current = id;
    });
  }, [state.isComplete, state.recommendation, state.responses, state.path, logRecommendation]);

  const handleResponse = useCallback((questionId: string, value: string) => {
    setState(prev => {
      const engine = createDecisionEngine(prev.responses);
      engine.addResponse(questionId, value);

      const newResponses = engine.getResponses();
      const nextQuestion = engine.getCurrentQuestion();
      const isComplete = engine.isComplete();
      const path = engine.getPath();
      const recommendation = isComplete ? engine.getRecommendation() : null;

      return {
        currentQuestion: nextQuestion ?? prev.currentQuestion,
        responses: newResponses,
        path,
        isComplete,
        recommendation,
      };
    });
  }, []);

  const handleBack = useCallback(() => {
    setState(prev => {
      if (prev.path.length === 0) return prev;
      const newPath = prev.path.slice(0, -1);
      const newResponses = prev.responses.filter(r => !prev.path.slice(-1).includes(r.questionId));
      const prevQuestion = newPath.length > 0 ? newPath[newPath.length - 1] : 'Q1';
      return {
        ...prev,
        path: newPath,
        responses: newResponses,
        currentQuestion: prevQuestion,
        isComplete: false,
        recommendation: null,
      };
    });
  }, []);

  const handleReset = useCallback(() => {
    setState(initialState);
    eventIdRef.current = null;
    recommendationLoggedRef.current = false;
  }, []);

  const handleToolOpen = useCallback((toolSlug: string, toolName: string) => {
    const eventId = eventIdRef.current;
    if (eventId) {
      // Fire-and-forget: the primary artifact (recommendation) is already persisted.
      // Tool open is a secondary update on the same row.
      logToolOpened(eventId, toolSlug, toolName);
    }
  }, [logToolOpened]);

  const question = !state.isComplete ? getQuestionById(state.currentQuestion) : null;
  const stepNumber = state.path.length + 1;
  const currentResponse = state.responses.find(r => r.questionId === state.currentQuestion);

  return (
    <div className="card border-primary-200 dark:border-primary-800 bg-gradient-to-br from-primary-50 to-blue-50 dark:from-slate-800 dark:to-slate-800">
      {/* Header — always visible */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 text-left group"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/40 border border-primary-200 dark:border-primary-700">
            <Wand2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-[var(--text-pri)] group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors">
              Not sure which tool to use?
            </h3>
            <p className="text-sm text-[var(--text-sec)]">
              Answer a few quick questions and get a personalised recommendation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {state.isComplete && !open && (
            <span className="text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/40 px-2 py-1 rounded-full">
              Recommendation ready
            </span>
          )}
          {open ? (
            <ChevronUp className="h-5 w-5 text-[var(--text-ter)]" />
          ) : (
            <ChevronDown className="h-5 w-5 text-[var(--text-ter)]" />
          )}
        </div>
      </button>

      {/* Expandable body */}
      {open && (
        <div className="mt-5 pt-5 border-t border-primary-200">

          {/* Matrix recommendations — shown when configuration declares object types + output shapes */}
          {matrixRecs && matrixRecs.primaryToolIds.length > 0 && (
            <div className="mb-5 pb-5 border-b border-primary-200">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-primary-500 flex-shrink-0" />
                <span className="text-sm font-semibold text-[var(--text-pri)]">
                  Recommended for your configuration
                </span>
              </div>
              {objectTypes && objectTypes.length > 0 && (
                <p className="text-xs text-[var(--text-ter)] mb-3">
                  {objectTypes.map(t => OBJECT_TYPE_LABELS[t]).join(', ')}
                  {outputShapes && outputShapes.length > 0 && (
                    <> &middot; {OUTPUT_SHAPE_HINTS[outputShapes[0]]}</>
                  )}
                </p>
              )}
              <div className="grid sm:grid-cols-2 gap-2">
                {matrixRecs.primaryToolIds.map((toolId) => {
                  const rec = matrixRecs.byToolId[toolId];
                  if (!rec) return null;
                  return (
                    <Link
                      key={toolId}
                      to={`/prioritization/tools/${toolId}`}
                      className="flex items-start gap-3 p-3 border border-primary-200 bg-[var(--bg-raised)] hover:border-primary-400 hover:bg-primary-50/60 dark:hover:bg-[var(--surface)] transition-colors"
                    >
                      <Sparkles className="h-3.5 w-3.5 text-primary-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[var(--text-pri)] truncate">
                          {rec.primaryToolName}
                        </div>
                        <div className="text-xs text-[var(--text-ter)] mt-0.5 leading-snug">
                          {rec.rationale}
                        </div>
                        {rec.cautions && rec.cautions.length > 0 && (
                          <ul className="mt-1.5 space-y-1">
                            {rec.cautions.map((caution, i) => (
                              <li
                                key={i}
                                className="border-l-2 border-[var(--mustard)] pl-2 text-[11px] leading-snug text-[var(--text-sec)]"
                              >
                                {caution}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-primary-400 flex-shrink-0 mt-0.5 ml-auto" />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {!state.isComplete ? (
            /* --- Question Step --- */
            <div>
              {/* Progress dots */}
              <div className="flex items-center gap-1.5 mb-4">
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <div
                    key={n}
                    className={`h-1.5 rounded-full transition-all ${
                      n < stepNumber
                        ? 'w-6 bg-primary-500'
                        : n === stepNumber
                        ? 'w-6 bg-primary-400'
                        : 'w-3 bg-[var(--hover-tint)]'
                    }`}
                  />
                ))}
                <span className="ml-2 text-xs text-[var(--text-ter)]">Step {stepNumber} of ~6</span>
              </div>

              {question && (
                <div>
                  <p className="font-semibold text-[var(--text-pri)] mb-3">{question.text}</p>
                  {question.helpText && (
                    <p className="text-xs text-[var(--text-ter)] mb-3">{question.helpText}</p>
                  )}

                  <div className="grid sm:grid-cols-2 gap-2">
                    {question.options?.map(option => {
                      const selected = currentResponse?.value === option.value;
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleResponse(question.id, option.value)}
                          className={`flex items-center gap-3 p-3  border-2 text-left transition-all text-sm ${
                            selected
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-300'
                              : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-primary-300 dark:hover:border-primary-600 hover:bg-primary-50/50 dark:hover:bg-[var(--surface)] text-[var(--text-sec)]'
                          }`}
                        >
                          <div className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                            selected ? 'border-primary-500 bg-primary-500' : 'border-[var(--rule)]'
                          }`} />
                          <span className="font-medium">{option.text}</span>
                        </button>
                      );
                    })}
                  </div>

                  {state.path.length > 0 && (
                    <button
                      onClick={handleBack}
                      className="mt-3 flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Back
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* --- Results --- */
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="h-5 w-5 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0" />
                <div>
                  <p className="font-semibold text-[var(--text-pri)]">Here's your recommendation</p>
                  {state.recommendation && (
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">{state.recommendation.reasoning}</p>
                  )}
                </div>
              </div>

              {state.recommendation && (
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  {state.recommendation.recommendedMethods.filter(method => {
                    const toolSlug = METHOD_TO_TOOL_PATH[method.id];
                    return !toolSlug || isToolEnabled(workspaceFeatures, toolSlug);
                  }).map(method => {
                    const toolSlug = METHOD_TO_TOOL_PATH[method.id];
                    const toolPath = toolSlug
                      ? `/prioritization/tools/${toolSlug}`
                      : null;

                    return (
                      <div
                        key={method.id}
                        className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-semibold text-[var(--text-pri)] text-sm leading-snug">
                            {method.name}
                          </h4>
                          <div className="flex gap-1 flex-shrink-0">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${COMPLEXITY_STYLES[method.complexity] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                              {method.complexity}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-[var(--text-sec)] mb-3 leading-relaxed">
                          {method.description}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${TIME_STYLES[method.timeRequired] ?? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'}`}>
                            {method.timeRequired} time
                          </span>
                          <span className="text-xs text-[var(--text-ter)]">{method.teamSize} team</span>
                          {toolPath ? (
                            <Link
                              to={toolPath}
                              onClick={() => handleToolOpen(toolSlug!, method.name)}
                              className="ml-auto flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                            >
                              Open tool
                              <ArrowRight className="h-3 w-3" />
                            </Link>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {state.recommendation && (
                <div className="text-xs text-[var(--text-ter)] bg-[var(--bg-raised)]/60 dark:bg-[var(--bg-raised)]/60 rounded p-3 mb-4">
                  <span className="font-medium capitalize text-[var(--text-sec)]">
                    {state.recommendation.confidence} confidence
                  </span>
                  {' — '}
                  {state.recommendation.confidenceExplanation}
                </div>
              )}

              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Start over
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
